<!-- Main Content -->
<div class="pt-32 pb-24">
  <div class="max-w-md mx-auto px-4 sm:px-6">
    <!-- Auth Card -->
    <div class="bg-white dark:bg-slate-900 rounded-2xl p-8 md:p-10 border border-slate-200 dark:border-slate-700 shadow-sm dark:shadow-slate-900/50">
      <!-- Logo & Title -->
      <div class="text-center mb-8">
        <div class="flex justify-center mb-6">
          <img src="assets/img/creditpathlogo.webp" alt="CreditPath Logo" class="w-12 h-12">
        </div>
        <h1 class="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
          {{ isSignIn ? 'Welcome back' : 'Create your account' }}
        </h1>
        <p class="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {{ isSignIn ? 'Sign in to access your CreditPath account' : 'Get started with CreditPath today' }}
        </p>
      </div>

      <!-- Banner Error -->
      @if (bannerError) {
        <div class="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <p class="text-sm text-red-700 dark:text-red-400">{{ bannerError }}</p>
          </div>
        </div>
      }

      <!-- Auth Form -->
      <form (ngSubmit)="onSubmit()" class="space-y-5">
        <!-- Email -->
        <div class="space-y-1.5">
          <label for="email" class="text-sm font-semibold text-slate-700 dark:text-slate-300">Email address</label>
          <input
            id="email"
            name="email"
            type="email"
            required
            [(ngModel)]="email"
            [class.border-red-400]="emailError"
            [class.focus:border-red-400]="emailError"
            [class.focus:ring-red-400]="emailError"
            class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 px-4 py-2.5 text-slate-900 dark:text-white focus:bg-white dark:focus:bg-slate-700 focus:border-indigo-600 dark:focus:border-indigo-500 focus:ring-1 focus:ring-indigo-600 dark:focus:ring-indigo-500 focus:outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
            placeholder="you@company.com">
          @if (emailError) {
            <p class="mt-1.5 text-sm text-red-600 dark:text-red-400">{{ emailError }}</p>
          }
        </div>

        <!-- Password -->
        <div class="space-y-1.5">
          <label for="password" class="text-sm font-semibold text-slate-700 dark:text-slate-300">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            required
            [(ngModel)]="password"
            (input)="onPasswordChange()"
            [class.border-red-400]="passwordError"
            [class.focus:border-red-400]="passwordError"
            [class.focus:ring-red-400]="passwordError"
            class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 px-4 py-2.5 text-slate-900 dark:text-white focus:bg-white dark:focus:bg-slate-700 focus:border-indigo-600 dark:focus:border-indigo-500 focus:ring-1 focus:ring-indigo-600 dark:focus:ring-indigo-500 focus:outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
            placeholder="••••••••">
          @if (passwordError) {
            <p class="mt-1.5 text-sm text-red-600 dark:text-red-400">{{ passwordError }}</p>
          }

          <!-- Password Requirements -->
          @if (showPasswordRequirements) {
            <div class="mt-3 p-4 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              <p class="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Password must contain:</p>
              <div class="space-y-1.5">
                <div class="flex items-center gap-2 text-xs" [class.text-green-600]="passwordValidation.minLength" [class.dark:text-green-400]="passwordValidation.minLength" [class.text-slate-500]="!passwordValidation.minLength" [class.dark:text-slate-400]="!passwordValidation.minLength">
                  <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    @if (passwordValidation.minLength) {
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    }
                    @if (!passwordValidation.minLength) {
                      <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" stroke-width="2"></circle>
                    }
                  </svg>
                  <span>At least 8 characters</span>
                </div>
                <div class="flex items-center gap-2 text-xs" [class.text-green-600]="passwordValidation.hasUppercase" [class.dark:text-green-400]="passwordValidation.hasUppercase" [class.text-slate-500]="!passwordValidation.hasUppercase" [class.dark:text-slate-400]="!passwordValidation.hasUppercase">
                  <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    @if (passwordValidation.hasUppercase) {
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    }
                    @if (!passwordValidation.hasUppercase) {
                      <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" stroke-width="2"></circle>
                    }
                  </svg>
                  <span>One uppercase letter (A-Z)</span>
                </div>
                <div class="flex items-center gap-2 text-xs" [class.text-green-600]="passwordValidation.hasLowercase" [class.dark:text-green-400]="passwordValidation.hasLowercase" [class.text-slate-500]="!passwordValidation.hasLowercase" [class.dark:text-slate-400]="!passwordValidation.hasLowercase">
                  <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    @if (passwordValidation.hasLowercase) {
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    }
                    @if (!passwordValidation.hasLowercase) {
                      <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" stroke-width="2"></circle>
                    }
                  </svg>
                  <span>One lowercase letter (a-z)</span>
                </div>
                <div class="flex items-center gap-2 text-xs" [class.text-green-600]="passwordValidation.hasNumber" [class.dark:text-green-400]="passwordValidation.hasNumber" [class.text-slate-500]="!passwordValidation.hasNumber" [class.dark:text-slate-400]="!passwordValidation.hasNumber">
                  <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    @if (passwordValidation.hasNumber) {
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    }
                    @if (!passwordValidation.hasNumber) {
                      <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" stroke-width="2"></circle>
                    }
                  </svg>
                  <span>One number (0-9)</span>
                </div>
                <div class="flex items-center gap-2 text-xs" [class.text-green-600]="passwordValidation.hasSpecialChar" [class.dark:text-green-400]="passwordValidation.hasSpecialChar" [class.text-slate-500]="!passwordValidation.hasSpecialChar" [class.dark:text-slate-400]="!passwordValidation.hasSpecialChar">
                  <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    @if (passwordValidation.hasSpecialChar) {
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    }
                    @if (!passwordValidation.hasSpecialChar) {
                      <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" stroke-width="2"></circle>
                    }
                  </svg>
                  <span>One special character (!&#64;#$%^&*)</span>
                </div>
              </div>
            </div>
          }
        </div>

        <!-- Remember Me & Forgot Password (Sign In only) -->
        @if (isSignIn) {
          <div class="flex items-center justify-between">
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" [(ngModel)]="rememberMe" name="rememberMe" class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500 dark:bg-slate-700">
              <span class="text-sm text-slate-600 dark:text-slate-400">Remember me</span>
            </label>
            <a routerLink="/forgot-password" class="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 transition-colors">
              Forgot password?
            </a>
          </div>
        }

        <!-- Submit Button -->
        <button
          type="submit"
          [disabled]="isLoading || (!isSignIn && !isPasswordValid)"
          class="w-full flex items-center justify-center gap-2 text-base font-medium px-6 py-3 rounded-lg transition-all shadow-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20 dark:shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed">
          @if (isLoading) {
            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          }
          <span>{{ isSignIn ? 'Sign in' : 'Create account' }}</span>
        </button>

        <!-- Toggle Sign In/Sign Up -->
        <div class="text-center text-sm">
          <span class="text-slate-500 dark:text-slate-400">
            {{ isSignIn ? "Don't have an account?" : 'Already have an account?' }}
          </span>
          <button type="button" (click)="toggleView()" class="ml-1 font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 transition-colors">
            {{ isSignIn ? 'Create one' : 'Sign in' }}
          </button>
        </div>

        <!-- Google Sign In (hidden when enableGoogleSignIn is false) -->
        @if (enableGoogleSignIn) {
          <div class="relative my-6">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-slate-200 dark:border-slate-700"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-4 bg-white dark:bg-slate-900 text-slate-400 dark:text-slate-500">Or continue with</span>
            </div>
          </div>
          <button
            type="button"
            (click)="handleGoogleSignIn()"
            [disabled]="isLoading"
            class="w-full flex items-center justify-center gap-3 px-6 py-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all text-slate-700 dark:text-slate-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            <svg class="w-5 h-5" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
            </svg>
            <span>Continue with Google</span>
          </button>
        }
      </form>
    </div>
  </div>
</div>

